import React from 'react';
import styled from 'styled-components';

const Panel = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
`;

const Header = styled.div`
  display: grid;
  gap: 6px;
  margin-bottom: 14px;
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
`;

const Description = styled.div`
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  color: #0f172a;
  background: white;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
`;

const CheckboxChip = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: white;
  font-size: 13px;
  font-weight: 600;
  color: #1d4ed8;
  cursor: pointer;

  input {
    margin: 0;
  }
`;

const HelperText = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
`;

const PinBulkElectricalDefaultsPanel = ({
  values,
  applyToAll,
  onValueChange,
  onApplyToAllChange,
  applyCount = 0,
  disabled = false,
  title = 'Bulk Electrical Defaults',
  description = 'Fill all three values, then check Apply to All to stamp them onto non-power pins. You can still fine-tune each pin below.',
  helperText,
}) => {
  const resolvedHelperText = helperText || (
    applyCount > 0
      ? `Applies to ${applyCount} non-power pin${applyCount === 1 ? '' : 's'}. Power and ground pins are skipped.`
      : 'No non-power pins are available to update yet.'
  );

  return (
    <Panel>
      <Header>
        <Title>{title}</Title>
        <Description>{description}</Description>
      </Header>

      <Grid>
        <Field>
          <FieldLabel>Current Limit (mA)</FieldLabel>
          <Input
            type="number"
            min="0"
            step="0.001"
            value={values?.current_limit_ma ?? ''}
            onChange={(event) => onValueChange?.('current_limit_ma', event.target.value)}
            placeholder="e.g. 20"
            disabled={disabled}
          />
        </Field>

        <Field>
          <FieldLabel>Voltage Min (V)</FieldLabel>
          <Input
            type="number"
            min="0"
            step="0.001"
            value={values?.voltage_min_v ?? ''}
            onChange={(event) => onValueChange?.('voltage_min_v', event.target.value)}
            placeholder="e.g. 0"
            disabled={disabled}
          />
        </Field>

        <Field>
          <FieldLabel>Voltage Max (V)</FieldLabel>
          <Input
            type="number"
            min="0"
            step="0.001"
            value={values?.voltage_max_v ?? ''}
            onChange={(event) => onValueChange?.('voltage_max_v', event.target.value)}
            placeholder="e.g. 3.3"
            disabled={disabled}
          />
        </Field>
      </Grid>

      <Footer>
        <CheckboxChip>
          <input
            type="checkbox"
            checked={Boolean(applyToAll)}
            onChange={(event) => onApplyToAllChange?.(event.target.checked)}
            disabled={disabled}
          />
          <span>Apply to All</span>
        </CheckboxChip>
        <HelperText>{resolvedHelperText}</HelperText>
      </Footer>
    </Panel>
  );
};

export default PinBulkElectricalDefaultsPanel;
